/**
 * What GrimoireCodexDB is, how it works, and what an account is for: the top of the home page.
 * The page is prerendered (scripts/prerender.tsx), and Google's OAuth verification reads this to
 * confirm the app's purpose and why it asks for Google sign-in, so it stays plain, public and
 * first in the page, and links the privacy policy.
 */
import type { ReactNode } from 'react'
import { Link } from 'react-router'

/** The home page's headline for each game scope. */
export const HOME_HEADLINES = {
  all: 'Tabletop RPGs and wargames, catalogued by the people who play them.',
  ttrpg: 'Tabletop RPG systems and books, catalogued by the people who play them.',
  wargame: 'Wargames and their books, catalogued by the people who play them.',
}

const STEPS: Array<{ title: string; body: ReactNode }> = [
  {
    title: 'Browse and search',
    body: 'Look up any game system, book, series, publisher or creator, with covers, credits, editions and links to where each is sold. No account needed.',
  },
  {
    title: 'Identify your books',
    body: (
      <>
        Library managers such as Grimoire match your files by ISBN, product code or file hash through the open{' '}
        <Link to="/developers">API and MCP server</Link>, then fill in their details.
      </>
    ),
  },
  {
    title: 'Fill the gaps',
    body: (
      <>
        Add a missing book or correct a record. Every change is an edit that is <Link to="/about#review">reviewed</Link>{' '}
        and kept in history.
      </>
    ),
  },
]

export function HomeIntro({
  headline,
  wargames = false,
  children,
}: {
  headline: string
  wargames?: boolean
  /** Live figures (the stat line), shown under the description. */
  children?: ReactNode
}) {
  return (
    <section className="home-intro">
      <h1 className="home-title">{headline}</h1>
      <p className="lede">
        GrimoireCodexDB is a free, community-maintained database of{' '}
        {wargames ? 'tabletop RPGs and wargames' : 'tabletop roleplaying games'}: their systems, books, series,
        publishers and creators. It supplies book and system details to{' '}
        <a href="https://github.com/hunter-read/grimoire" target="_blank" rel="noopener noreferrer">
          Grimoire
        </a>
        , the self-hosted TTRPG library manager, and to anyone else who needs them.
      </p>
      {children}
      <section className="home-how" aria-labelledby="how-title">
        <h2 id="how-title" className="home-how-title">
          How it works
        </h2>
        <ol className="home-steps">
          {STEPS.map((s) => (
            <li key={s.title}>
              <h3>{s.title}</h3>
              <p>{s.body}</p>
            </li>
          ))}
        </ol>
        {/* The same for everyone, so nothing moves when the session loads after the first paint. */}
        <p className="home-account">
          Anyone can browse GrimoireCodexDB or use its API without an account. To add or fix records, sign in with
          Discord, GitHub or Google. We use your name, email address and profile picture only to set up your account and
          credit your edits. See the <Link to="/privacy">privacy policy</Link> and <Link to="/terms">terms of use</Link>
          .
        </p>
      </section>
    </section>
  )
}
